import React from 'react';
import { Composite, Composition, CompositionComponent, CompositionProcess, Surcharge } from '../types/costwand.d';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx';
import CompositionViewer from './CompositionViewer.tsx';

interface CompositeCardProps {
  composite: Composite;
  surcharges: Surcharge[];
}

interface CompositionTotals {
  totalComponents: number;
  totalProcesses: number;
}

// Helper function to recursively count components and processes
const getCompositionTotals = (composition: Composition): CompositionTotals => {
  let totalComponents = 0;
  let totalProcesses = 0;

  // Count top-level processes
  if (composition.processes) {
    totalProcesses += composition.processes.length;
  }

  // Recursively count components and their processes/subcomponents
  const countRecursive = (components: CompositionComponent[]) => {
    components.forEach(comp => {
      totalComponents++; // Count this component
      if (comp.processes) {
        totalProcesses += comp.processes.length; // Count processes specific to this component
      }
      if (comp.subcomponents) {
        countRecursive(comp.subcomponents); // Recurse for subcomponents
      }
    });
  };

  if (composition.components) {
    countRecursive(composition.components);
  }

  return { totalComponents, totalProcesses };
};

const CompositeCard: React.FC<CompositeCardProps> = ({ composite, surcharges }) => {
  const { totalComponents, totalProcesses } = getCompositionTotals(composite.composition);

  // Calculate cost with surcharge
  const surchargeProfile = surcharges.find(s => s.id === composite.tax_profile_id);
  const surchargePercentage = surchargeProfile?.percentage || 0;
  const costWithOutSurcharge = composite.cost ? composite.cost / (1 + surchargePercentage / 100) : null;

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <CardTitle>{composite.description}</CardTitle>
        <CardDescription>Article No: {composite.article_number}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="space-y-2">
          <p><strong>Base Cost:</strong> ${costWithOutSurcharge?.toFixed(4) || 'N/A'}</p>
          <p><strong>Surcharge Profile:</strong> {composite._labels?.tax_profile_id || 'N/A'} ({surchargePercentage.toFixed(2)}%)</p>
          <p><strong>Cost with Surcharge:</strong> ${composite.cost?.toFixed(4) || 'N/A'}</p>
          <div className="pt-2 border-t mt-4">
            <h4 className="font-semibold text-md mb-1">Composition Summary:</h4>
            <p>Total Components: {totalComponents}</p>
            <p>Total Processes: {totalProcesses}</p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between gap-2 pt-4">
        <Button variant="outline" size="sm" asChild>
          <Link to={`/composition-edit/${composite.id}`}>Edit</Link>
        </Button>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="black" size="sm">
              View Composition
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Composition for {composite.description}</DialogTitle>
            </DialogHeader>
            <CompositionViewer composition={composite.composition} total_cost={costWithOutSurcharge?.toFixed(4)} />
          </DialogContent>
        </Dialog>
      </CardFooter>
    </Card>
  );
};

export default CompositeCard;